import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { UserSessionService } from '../services/user-session.service';
import { FeatureFlag } from '../enums/feature-flags.enums';
import { GuardConstants, SystemPageRoutes } from '../constants/constants';

// A route opts in with data: { featureFlag: FeatureFlag.X }, the way permissionsGuard reads
// data['permissions'] - so a second flag costs one route-data key and no new code.
export const featureFlagGuard: CanActivateFn = async (route, _state) => {
  const userSessionService = inject(UserSessionService);
  const router = inject(Router);

  const featureFlag = route.data['featureFlag'] as FeatureFlag | undefined;

  if (!featureFlag) {
    return true;
  }

  // Awaited rather than read straight off the signal: the flags fetch races the authorization fetch
  // userLoadedGuard awaits, so deciding early let a disabled feature's page load and surface the
  // API's own 403 text. The loader is idempotent, so a warm navigation resolves immediately.
  await waitForFeatureFlags(userSessionService);

  // Only an explicit server "off" blocks. A load that failed or timed out does not: a flag is not an
  // authorization boundary - permissionsGuard ahead of this one, and the API, still are - and
  // treating a blip as "off" would lock an organization out of a feature it has.
  return userSessionService.isFeatureBlocked(featureFlag)
    ? router.createUrlTree(SystemPageRoutes.FORBIDDEN())
    : true;
};

// Capped so a hung request cannot wedge navigation. On timeout the state is still Pending, which
// does not block, so the page loads and the API remains the enforcing gate.
async function waitForFeatureFlags(userSessionService: UserSessionService): Promise<void> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const timeout = new Promise<void>(resolve => {
    timeoutId = setTimeout(resolve, GuardConstants.FEATURE_FLAG_LOAD_TIMEOUT_MS());
  });

  try {
    await Promise.race([userSessionService.getFeatureFlags(), timeout]);
  } finally {
    clearTimeout(timeoutId);
  }
}
